import React, { useState, useRef } from 'react';
import { RadioButton } from 'primereact/radiobutton';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import traceabilityService, {
  TraceType,
  TimelineItem,
  RawMaterialPurchaseData,
  BatchData,
  OrderData,
  OilIntakeLog,
  OrderInvoice,
  BatchDetail,
} from '../../services/Traceability/traceabilityService';
import './traceability.scss';
import jsPDF from "jspdf";
import TestReportService from "../../services/TestReport/TestReport.service";
import { Dialog } from "primereact/dialog";
import centerLogo from "../../assests/images/DesiriLogo.png";
import qaSeal from "../../assests/images/badge.png";
import ReportPageHeader from "../shared/ReportPageHeader";

interface TraceOption {
  label: string;
  value: TraceType;
}

const traceOptions: TraceOption[] = [
  { label: 'Batch No', value: 'BatchId' },
  { label: 'Raw Material Invoice #', value: 'RawMaterialInvoiceNumber' },
  { label: 'Order #', value: 'OrderId' },
  { label: 'Order Invoice #', value: 'InvoiceNo' },
];

const Traceability: React.FC = () => {
  const toast = useRef<Toast>(null);
  const [traceType, setTraceType] = useState<TraceType>('BatchId');
  const [value, setValue] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [timeline, setTimeline] = useState<TimelineItem[]>([]);
  const [hasSearched, setHasSearched] = useState<boolean>(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewerTitle, setViewerTitle] = useState("Test Report Preview");
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfDoc, setPdfDoc] = useState<jsPDF | null>(null);
  const [pdfFilename, setPdfFilename] = useState<string>("report.pdf");
  const [openingReport, setOpeningReport] = useState(false);

  const normalizeTraceValue = (type: TraceType, rawValue: string): string => {
    const value = rawValue.trim();

    if (type === "BatchId") {
      // Accept formats: B00000064, b00000064, 00000064, 64
      const match = value.match(/^b?0*(\d+)$/i);
      if (match) {
        return match[1]; // ✅ only numeric ID
      }
    }

    return value;
  };

  const handleTrace = async () => {
    if (!value.trim()) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Validation',
        detail: 'Please enter a value to trace',
        life: 3000,
      });
      return;
    }

    if (traceType === "BatchId" && !/^\s*b?\d+\s*$/i.test(value)) {
      toast.current?.show({
        severity: "warn",
        summary: "Invalid Batch No",
        detail: "Please enter a valid Batch No",
        life: 3000,
      });
      return; // ⛔ stop execution
    }

    setLoading(true);
    setHasSearched(true);
    try {
      const normalizedValue = normalizeTraceValue(traceType, value);
      const response = await traceabilityService.trace(traceType, normalizedValue);
      if (response.isSuccess) {
        setTimeline(response.timeline);
        if (response.timeline.length === 0) {
          toast.current?.show({
            severity: 'info',
            summary: 'No Data',
            detail: 'No traceability data found for the provided parameter',
            life: 3000,
          });
        }
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: response.message || 'No traceability data found for the provided parameter',
          life: 3000,
        });
        setTimeline([]);
      }
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'No traceability data found for the provided parameter',
        life: 3000,
      });
      setTimeline([]);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setValue('');
    setTimeline([]);
    setHasSearched(false);
  };


  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleTrace();
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatDateTime = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  // Group timeline items by stage
  const rawMaterialItems = timeline.filter((item) => item.stage === 'RawMaterialPurchase');
  const batchItems = timeline.filter((item) => item.stage === 'Batch');
  const orderItems = timeline.filter((item) => item.stage === 'Order');

  const openTestReport = async (
    reportId: number,
    reportDate?: string | null
  ) => {
    try {
      setOpeningReport(true);
      const { url, doc, filename } =
        await TestReportService.getTestReportViewerPayload(reportId, {
          reportDate: reportDate ?? null,   // ✅ now defined
          centerLogoUrl: centerLogo,
          qaSealUrl: qaSeal,
        });

      setViewerTitle("Test Report Preview");
      setPdfUrl(url);
      setPdfDoc(doc);
      setPdfFilename(filename);
      setViewOpen(true);
    } catch (e) {
      console.error(e);
      toast.current?.show({
        severity: "error",
        summary: "Failed to open Test Report",
        life: 2500,
      });
    }
    finally {
      setOpeningReport(false); // ✅ stop loading
    }
  };


  const closeView = () => {
    setViewOpen(false);
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    setPdfUrl(null);
    setPdfDoc(null);
  };


  const renderRawMaterialCard = (item: TimelineItem, index: number) => {
    const data = item.data as RawMaterialPurchaseData;
    return (
      <div key={`raw-${data.purchaseId}-${index}`} className="trace-card">
        <div className="card-header">
          <span className="card-date">{formatDate(item.date)}</span>
          <span className="card-badge raw-material">Raw Material</span>
        </div>
        <div className="card-body">
          <div className="info-row">
            <span className="label">Invoice #</span>
            <span className="value">{data.invoiceNumber}</span>
          </div>
          <div className="info-row">
            <span className="label">Vendor</span>
            <span className="value">{data.vendor.firmInfo}</span>
          </div>
          <div className="info-row">
            <span className="label">Raw Material</span>
            <span className="value">{data.rawMaterialName}</span>
          </div>
          <div className="info-row">
            <span className="label">Category</span>
            <span className="value">{data.categoryName}</span>
          </div>
          <div className="info-grid">
            <div className="info-cell">
              <span className="cell-label">Invoiced Qty</span>
              <span className="cell-value">{data.invoicedQuantity}</span>
            </div>
            <div className="info-cell">
              <span className="cell-label">Accepted Qty</span>
              <span className="cell-value">{data.acceptedQuantity}</span>
            </div>
            <div className="info-cell">
              <span className="cell-label">Total Amount</span>
              <span className="cell-value">{formatCurrency(data.totalAmount)}</span>
            </div>
          </div>
          <div className="info-row">
            <span className="label">Unit</span>
            <span className="value">{data.posUnit}</span>
          </div>
          {/* {data.comments && (
            <div className="info-row">
              <span className="label">Comments</span>
              <span className="value">{data.comments}</span>
            </div>
          )} */}
        </div>
      </div>
    );
  };

  const formatTimeIST = (time?: string | null): string => {
    if (!time) return "—";

    let date: Date;

    // Case 1: Full ISO datetime → parse directly
    if (time.includes("T")) {
      date = new Date(time);
    }
    // Case 2: Time-only (HH:mm or HH:mm:ss)
    else {
      const today = new Date().toISOString().split("T")[0]; // YYYY-MM-DD
      date = new Date(`${today}T${time}`);
    }

    if (isNaN(date.getTime())) return "—";

    return date.toLocaleTimeString("en-IN", {
      timeZone: "Asia/Kolkata",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const renderOilIntakeLog = (log: OilIntakeLog, index: number) => (
    <div key={`log-${log.oilIntakeLogId}-${index}`} className="subsection-card">
      <div className="subsection-header">
        <i className="pi pi-cog"></i>
        <span>Oil Intake Log - {formatDate(log.submittedDate)}</span>
        <span className="subsection-badge oil ml-auto">#{log.oilIntakeLogNo}</span>
      </div>
      <div className="subsection-body">
        <div className="info-grid compact">
          <div className="info-cell time">
            <span className="cell-label">Time</span>
            <span className="cell-value">{formatTimeIST(log.startTime)} - {formatTimeIST(log.endTime)}</span>
          </div>
          <div className="info-cell">
            <span className="cell-label">Oil Seed</span>
            <span className="cell-value">{log.oilSeedName}</span>
          </div>
          <div className="info-cell">
            <span className="cell-label">Weight</span>
            <span className="cell-value">{log.oilSeedWeight} kg</span>
          </div>
          <div className="info-cell">
            <span className="cell-label">Moisture</span>
            <span className="cell-value">{log.moistureContent}%</span>
          </div>
        </div>
        <div className="info-row">
          <span className="label">Gaana</span>
          <span className="value">{log.gaanaDetails}</span>
        </div>
        <div className="info-row">
          <span className="label">Operator(s)</span>
          <span className="value">
            {log.operator1Name}
            {log.operator2Name && `, ${log.operator2Name}`}
          </span>
        </div>
        <div className="info-row">
          <span className="label">Bull(s)</span>
          <span className="value">
            {log.bull1Details}
            {log.bull2Details && `, ${log.bull2Details}`}
          </span>
        </div>
        <div className="info-row">
          <span className="label">Unit</span>
          <span className="value">
            {log.posUnit}
          </span>
        </div>
        {log.invoiceDetails && log.invoiceDetails.length > 0 && (
          <div className="invoice-details-section">
            <div className="mini-section-title">Invoice Details</div>
            {log.invoiceDetails.map((inv, idx) => (
              <div key={`inv-detail-${idx}`} className="mini-card">
                <span className="mini-label">#{inv.invoiceNo}</span>
                <span className="mini-value">{inv.rawMaterialName} - {inv.oilseedWeight} kg</span>
                <span className="mini-vendor">{inv.vendor.firmInfo}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderBatchCard = (item: TimelineItem, index: number) => {
    const data = item.data as BatchData;
    return (
      <div key={`batch-${data.batchId}-${index}`} className="trace-card">
        <div className="card-header batch-header">
          <span className="card-date">{formatDateTime(item.date)}</span>
          <div className="batch-actions">
            <span className="card-badge batch">#{data.batchNo}</span>

            {data.hasReport && data.reportId && (
              <Button
                icon="pi pi-file-pdf"
                className="p-button-text p-button-sm"
                tooltip="View Test Report"
                tooltipOptions={{ position: "top" }}
                disabled={openingReport}
                onClick={() => {
                  if (!data.hasReport || !data.reportId) return;
                  openTestReport(data.reportId, data.reportDate);
                }}
              />
            )}
          </div>
        </div>
        <div className="card-body">
          {data.productName && (
            <div className="info-row">
              <span className="label">Product</span>
              <span className="value">{data.productName}</span>
            </div>
          )}

          {data.subCategoryName && (
            <div className="info-row">
              <span className="label">Sub Category</span>
              <span className="value">{data.subCategoryName}</span>
            </div>
          )}
          <div className="info-grid">
            <div className="info-cell">
              <span className="cell-label">Manufacture</span>
              <span className="cell-value">{formatDate(data.manufactureDate)}</span>
            </div>
            <div className="info-cell">
              <span className="cell-label">Expiry</span>
              <span className="cell-value">{formatDate(data.expiryDate)}</span>
            </div>
            <div className="info-cell">
              <span className="cell-label">Stock</span>
              <span className="cell-value">{data.stock}</span>
            </div>
            <div className="info-cell">
              <span className="cell-label">Available</span>
              <span className="cell-value">{data.availableStock}</span>
            </div>
          </div>
          {data.posUnit && (
            <div className="info-row">
              <span className="label">Unit</span>
              <span className="value">{data.posUnit}</span>
            </div>
          )}
          {data.oilIntakeLogs && data.oilIntakeLogs.length > 0 && (
            <div className="subsection">
              <div className="subsection-title">
                <i className="pi pi-list"></i>
                Oil Intake Logs ({data.oilIntakeLogs.length})
              </div>
              {data.oilIntakeLogs.map((log, idx) => renderOilIntakeLog(log, idx))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderInvoice = (invoice: OrderInvoice, index: number) => (
    <div key={`order-inv-${invoice.orderInvoiceId}-${index}`} className="subsection-card">
      <div className="subsection-header">
        <i className="pi pi-file"></i>
        <span>Invoice #{invoice.invoiceNo}</span>
      </div>
      <div className="subsection-body">
        <div className="info-grid compact">
          <div className="info-cell">
            <span className="cell-label">Date</span>
            <span className="cell-value">{formatDateTime(invoice.invoiceDate)}</span>
          </div>
          <div className="info-cell">
            <span className="cell-label">Payment</span>
            <span className="cell-value">{invoice.paymentMode}</span>
          </div>
          <div className="info-cell">
            <span className="cell-label">Taxable</span>
            <span className="cell-value">{formatCurrency(invoice.taxableAmount)}</span>
          </div>
          <div className="info-cell">
            <span className="cell-label">Tax</span>
            <span className="cell-value">{formatCurrency(invoice.taxAmount)}</span>
          </div>
          <div className="info-cell">
            <span className="cell-label">Total</span>
            <span className="cell-value highlight">{formatCurrency(invoice.grossAmount)}</span>
          </div>
          <div className="info-cell">
            <span className="cell-label">POS</span>
            <span className="cell-value">{invoice.posCode}</span>
          </div>
        </div>
        {invoice.products && invoice.products.length > 0 && (
          <div className="products-list">
            {invoice.products.map((prod, idx) => (
              <div key={`inv-prod-${idx}`} className="product-item">
                <span className="prod-name">{prod.productName}</span>
                <span className="prod-qty">x{prod.invoicedQuantity}</span>
                <span className="prod-price">{formatCurrency(prod.unitPrice)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderOrderCard = (item: TimelineItem, index: number) => {
    const data = item.data as OrderData;
    return (
      <div key={`order-${data.orderId}-${index}`} className="trace-card">
        <div className="card-header">
          <span className="card-date">{formatDateTime(item.date)}</span>
          <span className={`card-badge order ${data.orderStatus.toLowerCase()}`}>
            {data.orderStatus}
          </span>
        </div>
        <div className="card-body">
          <div className="order-id">Order #{data.orderId}</div>
          <div className="info-row">
            <span className="label">Customer</span>
            <span className="value">{data.customer.name}</span>
          </div>
          <div className="info-row">
            <span className="label">Contact</span>
            <span className="value">{data.customer.primaryContactNbr}</span>
          </div>
          <div className="info-row">
            <span className="label">Address</span>
            <span className="value">{data.customer.address}</span>
          </div>
          <div className="info-grid">
            <div className="info-cell">
              <span className="cell-label">Amount</span>
              <span className="cell-value highlight">{formatCurrency(data.grossAmount)}</span>
            </div>
            <div className="info-cell">
              <span className="cell-label">POS</span>
              <span className="cell-value">{data.posCode}</span>
            </div>
          </div>
          {data.products && data.products.length > 0 && (
            <div className="products-section">
              <div className="mini-section-title">Products</div>
              {data.products.map((prod, idx) => {
                console.log('Product data:', prod);
                console.log('Batches:', prod.batches);
                return (
                  <div key={`order-prod-${idx}`} className="product-item-wrapper">
                    <div className="product-item">
                      <span className="prod-name">{prod.productName}</span>
                      <span className="prod-qty">x{prod.quantity}</span>
                      <span className="prod-price">{formatCurrency(prod.unitPrice)}</span>
                    </div>
                    {prod.batches && prod.batches.length > 0 && (
                      <div className="batch-details">
                        {prod.batches.map((batch, bIdx) => (
                          <span key={`batch-${bIdx}`} className="batch-tag">
                            #B{String(batch.batchId).padStart(8, '0')} × {batch.quantity}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {data.invoices && data.invoices.length > 0 && (
            <div className="subsection">
              <div className="subsection-title">
                <i className="pi pi-file"></i>
                Invoices ({data.invoices.length})
              </div>
              {data.invoices.map((inv, idx) => renderInvoice(inv, idx))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderSection = (
    title: string,
    icon: string,
    items: TimelineItem[],
    renderCard: (item: TimelineItem, index: number) => React.ReactNode
  ) => {
    if (items.length === 0) return null;
    return (
      <div className="trace-section">
        <div className="section-header">
          <i className={`pi ${icon}`}></i>
          <span>{title}</span>
          <span className="section-count">{items.length}</span>
        </div>
        <div className="section-cards">
          {items.map((item, index) => renderCard(item, index))}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className={`traceability-page ${openingReport ? "page-loading" : ""}`}>
        <Toast ref={toast} />

        <ReportPageHeader
          icon="pi-sitemap"
          title="Traceability"
          subtitle="Track batches, invoices, raw material purchases, and order history from one place"
          filterLabel="Filter Traceability"
        >
          <div className="trace-options">
            <label className="options-label">Trace By</label>
            <div className="radio-group">
              {traceOptions.map((option) => (
                <div key={option.value} className="radio-item">
                  <RadioButton
                    inputId={option.value}
                    name="traceType"
                    value={option.value}
                    checked={traceType === option.value}
                    onChange={(e) => setTraceType(e.value)}
                  />
                  <label htmlFor={option.value}>{option.label}</label>
                </div>
              ))}
            </div>
          </div>

          <div className="trace-input-group">
            <div className="input-wrapper">
              <label htmlFor="traceValue">Value</label>
              <InputText
                id="traceValue"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={`Enter ${traceOptions.find(o => o.value === traceType)?.label}`}
                className="trace-input"
              />
            </div>
            <Button
              label="Trace"
              icon="pi pi-search"
              onClick={handleTrace}
              disabled={loading}
              className="trace-button"
            />
            <Button
              label="Reset"
              icon="pi pi-refresh"
              onClick={handleReset}
              disabled={loading}
              className="p-button-secondary"
            />
          </div>
        </ReportPageHeader>

        {loading && (
          <div className="loading-container">
            <ProgressSpinner style={{ width: '50px', height: '50px' }} />
          </div>
        )}

        {!loading && hasSearched && timeline.length === 0 && (
          <div className="no-data">
            <i className="pi pi-inbox"></i>
            <p>No traceability data found</p>
          </div>
        )}

        {!loading && timeline.length > 0 && (
          <div className="trace-results">
            {renderSection('Raw Material Purchase', 'pi-box', rawMaterialItems, renderRawMaterialCard)}
            {renderSection('Batch Information', 'pi-tags', batchItems, renderBatchCard)}
            {renderSection('Orders', 'pi-shopping-cart', orderItems, renderOrderCard)}
          </div>
        )}
      </div>

      <Dialog
        header={viewerTitle}
        visible={viewOpen}
        onHide={closeView}
        dismissableMask
        style={{ width: "90vw", maxWidth: 1200 }}
        contentStyle={{ padding: 0 }}
      >
        {openingReport ? (
          <div className="p-6 flex justify-content-center align-items-center">
            <ProgressSpinner style={{ width: "50px", height: "50px" }} />
          </div>
        ) : pdfUrl ? (
          <iframe
            title="Test Report PDF"
            src={pdfUrl}
            style={{ border: 0, width: "100%", height: "80vh" }}
          />
        ) : (
          <div className="p-4">Loading…</div>
        )}
      </Dialog>
    </>
  );
};

export default Traceability;
